/**
 * NBM Predictive Search
 *
 * Typeahead search against Shopify's predictive search API.
 * On desktop: results render in a dropdown beneath the header search bar.
 * On mobile: results render inside the full-screen search modal.
 *
 * HTML contract:
 *   .js-search-input        — text input (inside .header__search or .search-modal__bar)
 *   .search-results         — container where results are rendered
 *   .js-search-clear        — clear button
 *   .js-search-modal-open   — button that opens the mobile modal
 *   .js-search-modal-close  — button that closes the mobile modal
 *   #search-modal           — the full-screen modal element
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 300;
  const SEARCH_ENDPOINT = '/search/suggest.json';
  const RESOURCE_TYPES = 'product,collection';
  const RESOURCE_LIMIT = 12;

  let debounceTimer = null;
  let activeController = null;
  let currentQuery = '';

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

/** Return the results container that's currently active. */
  function getResultsContainer() {
    const modal = document.getElementById('search-modal');
    if (modal && modal.classList.contains('is-open')) {
      return modal.querySelector('.search-results');
    }
    return document.querySelector('.header__search .search-results');
  }

  function clearResults() {
    const container = getResultsContainer();
    if (container) container.innerHTML = '';
  }

  function showLoading() {
    const container = getResultsContainer();
    if (!container) return;
    container.innerHTML =
      '<div style="display:flex;justify-content:center;padding:24px;">' +
      '<svg width="24" height="24" viewBox="0 0 24 24" style="animation:nbm-spin .8s linear infinite;">' +
      '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="50 20" stroke-linecap="round"/>' +
      '</svg></div>';

    if (!document.getElementById('nbm-search-keyframes')) {
      const style = document.createElement('style');
      style.id = 'nbm-search-keyframes';
      style.textContent = '@keyframes nbm-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Fetch & Render                                                     */
  /* ------------------------------------------------------------------ */

  function fetchResults(query) {
    if (activeController) activeController.abort();
    activeController = new AbortController();
    currentQuery = query;

    const url =
      `${SEARCH_ENDPOINT}?q=${encodeURIComponent(query)}` +
      `&resources[type]=${RESOURCE_TYPES}` +
      `&resources[limit]=${RESOURCE_LIMIT}`;

    showLoading();

    fetch(url, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' },
      signal: activeController.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error('Search request failed');
        return response.json();
      })
      .then((data) => { renderResults(data, query); })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[NBMSearch] Fetch error:', error);
        renderNoResults(query);
      });
  }

  function renderViewAllFooter(query) {
    const viewAllUrl = `/search?q=${encodeURIComponent(query)}&type=product`;
    return (
      `<div class="search-footer">` +
      `<a href="${escapeAttr(viewAllUrl)}" class="search-footer__link">` +
      `View all results for &ldquo;${escapeHTML(query)}&rdquo;` +
      `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>` +
      `</a></div>`
    );
  }

  function renderResults(data, query) {
    const container = getResultsContainer();
    if (!container) return;

    const resources = data.resources && data.resources.results;
    if (!resources) { renderNoResults(query); return; }

    const products = resources.products || [];
    const collections = resources.collections || [];

    if (products.length === 0 && collections.length === 0) {
      renderNoResults(query);
      return;
    }

    let html = '';

    if (collections.length > 0) {
      html += '<div class="search-section">';
      html += '<h3 class="search-section__title" style="font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-tertiary);padding:8px 12px;margin:0;">Collections</h3>';
      html += '<ul class="search-section__list" style="list-style:none;margin:0;padding:0;">';
      for (const collection of collections) {
        html += renderCollectionItem(collection);
      }
      html += '</ul></div>';
    }

    if (products.length > 0) {
      html += '<div class="search-section">';
      html += '<h3 class="search-section__title" style="font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-tertiary);padding:8px 12px;margin:0;">Products</h3>';
      html += '<ul class="search-section__list" style="list-style:none;margin:0;padding:0;">';
      for (const product of products) {
        html += renderProductItem(product);
      }
      html += '</ul></div>';
    }

    html += renderViewAllFooter(query);

    container.innerHTML = html;
  }

  function renderProductItem(product) {
    const image = product.image || '';
    const thumb = image
      ? `<img src="${escapeAttr(getSizedImageUrl(image, '80x80'))}" srcset="${escapeAttr(getSizedImageUrl(image, '80x80'))} 2x, ${escapeAttr(getSizedImageUrl(image, '120x120'))} 3x" alt="${escapeAttr(product.title)}" width="40" height="40" loading="lazy" style="object-fit:cover;border-radius:4px;flex-shrink:0;">`
      : '<span style="width:40px;height:40px;background:var(--color-bg-tertiary);border-radius:4px;flex-shrink:0;display:block;"></span>';

    const price = product.price != null ? '$' + parseFloat(product.price).toFixed(2) : '';

    return '<li>' +
      `<a href="${escapeAttr(product.url)}" class="search-result" style="display:flex;align-items:center;gap:12px;padding:8px 12px;text-decoration:none;color:inherit;">` +
      thumb +
      '<span style="flex:1;min-width:0;">' +
      `<span style="display:block;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHTML(product.title)}</span>` +
      (price ? `<span style="display:block;font-size:13px;color:var(--color-text-secondary);">${price}</span>` : '') +
      '</span>' +
      '</a></li>';
  }

  function renderCollectionItem(collection) {
    const image = collection.image || (collection.featured_image && collection.featured_image.url) || '';
    const thumb = image
      ? `<img src="${escapeAttr(getSizedImageUrl(image, '80x80'))}" srcset="${escapeAttr(getSizedImageUrl(image, '80x80'))} 2x, ${escapeAttr(getSizedImageUrl(image, '120x120'))} 3x" alt="${escapeAttr(collection.title)}" width="40" height="40" loading="lazy" style="object-fit:cover;border-radius:4px;flex-shrink:0;">`
      : '<span style="width:40px;height:40px;background:var(--color-bg-tertiary);border-radius:4px;flex-shrink:0;display:block;"></span>';

    return '<li>' +
      `<a href="${escapeAttr(collection.url)}" class="search-result" style="display:flex;align-items:center;gap:12px;padding:8px 12px;text-decoration:none;color:inherit;">` +
      thumb +
      '<span style="flex:1;min-width:0;">' +
      `<span style="display:block;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHTML(collection.title)}</span>` +
      '</span>' +
      '</a></li>';
  }

  function renderNoResults(query) {
    const container = getResultsContainer();
    if (!container) return;
    let html = '<p style="text-align:center;padding:24px;color:var(--color-text-tertiary);font-size:14px;margin:0;">No results found</p>';
    if (query) html += renderViewAllFooter(query);
    container.innerHTML = html;
  }

  /* ------------------------------------------------------------------ */
  /*  String Utilities                                                   */
  /* ------------------------------------------------------------------ */

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function getSizedImageUrl(url, size) {
    if (!url || typeof url !== 'string') return '';
    const match = url.match(/^(.*?)(\.\w+)(\?.*)?$/);
    if (match) {
      return `${match[1]}_${size}${match[2]}${match[3] || ''}`;
    }
    return url;
  }

  /* ------------------------------------------------------------------ */
  /*  Search Modal                                                       */
  /* ------------------------------------------------------------------ */

  function openSearchModal() {
    const modal = document.getElementById('search-modal');
    if (!modal) return;
    modal.classList.add('is-open');
    modal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    const input = modal.querySelector('.js-search-input');
    if (input) setTimeout(() => input.focus(), 50);
  }

  function closeSearchModal() {
    const modal = document.getElementById('search-modal');
    if (!modal) return;
    const resultsEl = modal.querySelector('.search-results');
    if (resultsEl) resultsEl.innerHTML = '';
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    const input = modal.querySelector('.js-search-input');
    if (input) {
      input.value = '';
      updateClearButton(input);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Event Handling                                                     */
  /* ------------------------------------------------------------------ */

  function updateClearButton(input) {
    const parent = input.closest('.header__search') || input.closest('.search-modal__bar');
    const clearBtn = parent ? parent.querySelector('.js-search-clear') : null;
    if (clearBtn) clearBtn.hidden = !input.value.trim();
  }

  function onInput(event) {
    const query = event.target.value.trim();
    clearTimeout(debounceTimer);
    updateClearButton(event.target);

    if (!query) {
      clearResults();
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchResults(query);
    }, DEBOUNCE_MS);
  }

  function onFocus(event) {
    const query = event.target.value.trim();
    if (query) fetchResults(query);
  }

  // Modal open/close
  document.addEventListener('click', (event) => {
    if (event.target.closest('.js-search-modal-open')) {
      openSearchModal();
      return;
    }
    if (event.target.closest('.js-search-modal-close')) {
      closeSearchModal();
      return;
    }
    // Close modal when clicking a search result link
    if (event.target.closest('#search-modal .search-result')) {
      closeSearchModal();
      return;
    }
  });

  // Input focus
  document.addEventListener('focusin', (event) => {
    if (event.target.matches && event.target.matches('.js-search-input')) {
      onFocus(event);
    }
  });

  // Input typing
  document.addEventListener('input', (event) => {
    if (event.target.matches && event.target.matches('.js-search-input')) {
      onInput(event);
    }
  });

  // Clear button
  document.addEventListener('click', (event) => {
    const clearBtn = event.target.closest('.js-search-clear');
    if (clearBtn) {
      event.preventDefault();
      const bar = clearBtn.closest('.header__search') || clearBtn.closest('.search-modal__bar');
      const input = bar ? bar.querySelector('.js-search-input') : document.querySelector('.js-search-input');
      if (input) {
        input.value = '';
        input.focus();
        updateClearButton(input);
      }
      clearResults();
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const modal = document.getElementById('search-modal');
      if (modal && modal.classList.contains('is-open')) {
        closeSearchModal();
      } else {
        clearResults();
        const input = document.querySelector('.header__search .js-search-input');
        if (input) input.blur();
      }
    }
  });

  // Close desktop dropdown when clicking outside the search bar
  document.addEventListener('click', (event) => {
    const modal = document.getElementById('search-modal');
    if (modal && modal.classList.contains('is-open')) return;
    if (!event.target.closest('.header__search')) {
      clearResults();
    }
  });
})();
